import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { EndorsementError } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';
import { useEndorsementForm, useEndorsementSubmit } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';

import { useEndorsementFlow } from '../useEndorsementFlow';
import { useEndorsementImageUpload } from '../useEndorsementImageUpload';

export type { EndorsementError } from 'src/app/pages/starterDashboardEndorsements/shared/hooks';

type UseAddEndorsementProps = { petitionSlug: string; petitionId: string };
type FormType = ReturnType<typeof useEndorsementForm>;
type ImageUploadType = ReturnType<typeof useEndorsementImageUpload>;
type FlowActionsType = ReturnType<typeof useEndorsementFlow>['actions'];
type FlowDataType = ReturnType<typeof useEndorsementFlow>['data'];

function buildData(form: FormType, flowData: FlowDataType, imageUpload: ImageUploadType) {
	return {
		formData: form.formData,
		isSubmitting: form.isSubmitting,
		statementCharCount: form.statementCharCount,
		statementMaxLength: form.statementMaxLength,
		isHelpModalOpen: flowData.isHelpModalOpen,
		isSubmitModalOpen: flowData.isSubmitModalOpen,
		isTermsAgreed: flowData.isTermsAgreed,
		error: form.error,
		showValidation: form.showValidation,
		image: imageUpload.data.image,
		imageCrop: imageUpload.data.imageCrop,
		imageUploadErrorMessage: imageUpload.data.errorMessage,
	};
}

type BuildActionsParams = {
	form: FormType;
	flowActions: FlowActionsType;
	handlers: { handleSubmit: () => void; handleNext: () => void; confirmSubmit: () => Promise<void> };
	imageUpload: ImageUploadType;
};

function buildActions({ form, flowActions, handlers, imageUpload }: BuildActionsParams) {
	return {
		handleFieldChange: form.handleFieldChange,
		handlePhotoUpload: form.handlePhotoUpload,
		handleSubmit: handlers.handleSubmit,
		handleNext: handlers.handleNext,
		handleBack: flowActions.handleBack,
		handleHelpClick: flowActions.handleHelpClick,
		openHelpModal: flowActions.openHelpModal,
		closeHelpModal: flowActions.closeHelpModal,
		openSubmitModal: flowActions.openSubmitModal,
		closeSubmitModal: flowActions.closeSubmitModal,
		confirmSubmit: handlers.confirmSubmit,
		clearError: form.clearError,
		handleTermsChange: flowActions.handleTermsChange,
		onImageCrop: imageUpload.actions.onImageCrop,
	};
}

// Sync image from upload context to form data
function useSyncImageToForm(image: string | undefined, handlePhotoUpload: (url: string) => void) {
	useEffect(() => {
		if (image) handlePhotoUpload(image);
	}, [image, handlePhotoUpload]);
}

type UseHandlersParams = {
	flowActions: FlowActionsType;
	track: ReturnType<typeof useTracking>;
	confirmSubmit: () => Promise<void>;
};

function useHandlers({ flowActions, track, confirmSubmit }: UseHandlersParams) {
	const handleNext = useCallback(() => {
		void track('petition_manage_endorsements_next_click');
		// Form validation is handled by React Hook Form in the Form component
		// No need for duplicate validation here
		flowActions.openSubmitModal();
	}, [track, flowActions]);

	const handleSubmit = useCallback(() => {
		flowActions.openSubmitModal();
	}, [flowActions]);

	return { handleNext, handleSubmit, confirmSubmit };
}

export function useAddEndorsement({ petitionSlug, petitionId }: UseAddEndorsementProps) {
	const track = useTracking();
	const utilityContext = useUtilityContext();
	const form = useEndorsementForm();
	const imageUpload = useEndorsementImageUpload();
	const { data: flowData, actions: flowActions } = useEndorsementFlow({ petitionSlug });

	useSyncImageToForm(imageUpload.data.image, form.handlePhotoUpload);

	const onSuccess = useCallback(() => {
		flowActions.closeSubmitModal();
		flowActions.navigateToEditPage();
	}, [flowActions]);

	const onError = useCallback(
		(error: EndorsementError) => {
			form.setError(error);
			flowActions.closeSubmitModal();
		},
		[form, flowActions],
	);

	const { submit: submitEndorsement } = useEndorsementSubmit({
		petitionId,
		formData: form.formData,
		utilityContext,
		track,
		onSuccess,
		onError,
		setIsSubmitting: form.setIsSubmitting,
		uploader: imageUpload.uploader,
		image: imageUpload.data.image,
	});

	const confirmSubmit = useCallback(async () => {
		void track('petition_manage_confirm_endorsement_modal_add_endorsement_click');
		await submitEndorsement();
	}, [track, submitEndorsement]);

	const handlers = useHandlers({
		flowActions,
		track,
		confirmSubmit,
	});

	return {
		data: buildData(form, flowData, imageUpload),
		actions: buildActions({ form, flowActions, handlers, imageUpload }),
		uploader: imageUpload.uploader,
	};
}
